/**
 * ContractActionBar - Barra de ações contextuais para contratos
 * Renderiza botões diferentes baseado no status atual do contrato.
 *
 * RELEASE: v1.0.0 - 2026-02-16
 * Status suportados: draft, generated, sent, signed, cancelled, expired
 *
 * RELEASE: v1.1.0 - 2026-02-18
 * Adicionado: detecção de erro de créditos Autentique (unavailable_credits)
 * com mensagem contextual e link direto para o painel de faturamento
 *
 * RELEASE: v1.2.0 - 2026-02-18
 * Substituído download de PDF assinado do Storage por link direto da Autentique.
 * PDF assinado agora abre via external_url (files.signed do webhook document.finished).
 * Removido: spinner "Aguardando PDF Assinado...", prop signedPdfStoragePath.
 * Adicionado: prop externalUrl para exibir botão "Ver PDF Assinado".
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileText, Send, RefreshCw, XCircle, Download, Loader2, RotateCcw, AlertTriangle, FileCheck, Trash2 } from 'lucide-react';
import { useContractActions } from '@/hooks/useContractActions';
import { ContractProcessingStatus } from './ContractProcessingStatus';
import type { ContractStatus } from '@/types/contracts';

// =============================================================================
// TIPOS
// =============================================================================

interface ContractActionBarProps {
  /** ID do contrato */
  contractId: string;
  /** ID do tenant ativo */
  tenantId: string;
  /** Status atual do contrato */
  status: ContractStatus;
  /** Caminho do PDF original no storage (quando disponível) */
  pdfStoragePath?: string | null;
  /**
   * URL externa do PDF assinado fornecida pela Autentique via campo files.signed
   * do webhook document.finished. Quando disponível, exibe botão "Ver PDF Assinado".
   */
  externalUrl?: string | null;
  /** Timestamp de quando foi enfileirado (ISO string) - usado para detecção de timeout */
  queuedAt?: string | null;
  /** Timestamp de quando foi enviado para assinatura - usado para detectar etapa do erro */
  sentAt?: string | null;
  /** Timestamp de quando foi assinado - usado para detectar etapa do erro */
  signedAt?: string | null;
  /**
   * Mensagem de erro salva no banco pelo worker ao falhar.
   * Usada para exibir mensagem contextual ao usuário.
   * Ex: "Autentique: unavailable_credits"
   */
  queueError?: string | null;
  /** Callback executado após exclusão bem-sucedida do rascunho */
  onDeleteSuccess?: () => void;
  /** v1.3.0: Permissão de edição (gera PDF, envia/reenvia, status, retry, signatários) */
  canEdit?: boolean;
  /** v1.3.0: Permissão de exclusão (Excluir Rascunho, Cancelar contrato) */
  canDelete?: boolean;
}

// =============================================================================
// COMPONENTE
// =============================================================================

/**
 * Barra de ações contextuais para o fluxo de contrato.
 * Exibe botões relevantes conforme o status atual.
 */
export function ContractActionBar({
  contractId,
  tenantId,
  status,
  pdfStoragePath,
  externalUrl,
  queuedAt,
  sentAt,
  signedAt,
  queueError,
  onDeleteSuccess,
  canEdit = true,
  canDelete = true,
}: ContractActionBarProps) {
  const {
    generatePdf,
    sendForSignature,
    checkStatus,
    cancelContract,
    downloadPdf,
    retrySaveSignedPdf,
    resendSignature,
    deleteContract,
  } = useContractActions(contractId, tenantId);

  // Status finais não possuem ações
  if (status === 'cancelled' || status === 'expired') {
    return null;
  }

  /** Status intermediários de processamento assíncrono */
  const isProcessing = (
    status === 'queued_pdf' ||
    status === 'processing_pdf' ||
    status === 'queued_signature' ||
    status === 'processing_signature'
  );

  // Durante processamento, renderiza o stepper visual com timeout e retry
  if (isProcessing) {
    /** Callback para re-enfileirar a ação conforme o tipo de processamento */
    const handleRetry = () => {
      if (status === 'queued_pdf' || status === 'processing_pdf') {
        generatePdf.mutate();
      } else {
        sendForSignature.mutate();
      }
    };

    return (
      <ContractProcessingStatus
        status={status as 'queued_pdf' | 'processing_pdf' | 'queued_signature' | 'processing_signature'}
        queuedAt={queuedAt}
        onRetry={handleRetry}
        isRetrying={generatePdf.isPending || sendForSignature.isPending}
      />
    );
  }

  // Status de erro — detecta em qual etapa ocorreu e retoma somente a partir dela
  if (status === 'error') {
    /**
     * Lógica de detecção da etapa do erro:
     * - signed_at + pdf_storage_path preenchidos → erro foi no save_signed_pdf (última etapa)
     * - pdf_storage_path preenchido mas sem sent_at → erro foi no envio para assinatura
     * - nenhum → erro foi na geração do PDF (início do fluxo)
     */
    const erroredAtSavePdf = !!signedAt && !!pdfStoragePath;
    const erroredAtSignature = !signedAt && !!pdfStoragePath && !sentAt;

    const handleRetryError = () => {
      if (erroredAtSavePdf) {
        // Falhou apenas na captura do PDF assinado — re-enfileira somente essa etapa
        retrySaveSignedPdf.mutate();
      } else if (erroredAtSignature || !!pdfStoragePath) {
        // Falhou no envio para assinatura — PDF já existe, apenas reenvia
        sendForSignature.mutate();
      } else {
        // Falhou na geração do PDF — reinicia desde o início
        generatePdf.mutate();
      }
    };

    // Rótulo dinâmico conforme a etapa
    const retryLabel = erroredAtSavePdf
      ? 'Recuperar PDF Assinado'
      : erroredAtSignature
      ? 'Reenviar para Assinatura'
      : 'Gerar PDF Novamente';

    const isPending = retrySaveSignedPdf.isPending || sendForSignature.isPending || generatePdf.isPending;

    /**
     * Detecção de erro de créditos insuficientes na Autentique.
     * O worker salva "Autentique: unavailable_credits" em contracts.queue_error
     * quando a API da Autentique rejeita a criação do documento por falta de créditos.
     */
    const isCreditsError = queueError?.toLowerCase().includes('unavailable_credits');

    // Erro de créditos insuficientes — exibe mensagem contextual com link para recarregar
    if (isCreditsError) {
      return (
        <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          {/* Cabeçalho do erro */}
          <div className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Créditos insuficientes na Autentique</span>
          </div>
          {/* Mensagem explicativa */}
          <div className="text-xs text-muted-foreground space-y-0.5 pl-6">
            <p>Sua conta não possui créditos disponíveis para enviar documentos.</p>
            <p>Recarregue seu plano em <strong>Autentique → Planos e pagamento</strong>.</p>
          </div>
          {/* Botões de ação */}
          <div className="flex flex-wrap gap-2 pl-6">
            {/* Abre painel de faturamento da Autentique em nova aba */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open('https://app.autentique.com.br/dashboard/faturas', '_blank', 'noopener,noreferrer')}
            >
              Abrir Painel Autentique
            </Button>
            {/* Botão de retry para após recarregar os créditos — gated por canEdit */}
            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRetryError}
                disabled={isPending}
              >
                {isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <RotateCcw />
                )}
                Tentar Novamente
              </Button>
            )}
          </div>
        </div>
      );
    }

    /**
     * Detecção de conta Autentique pendente de verificação biométrica do titular.
     * O worker salva a mensagem prefixada com "AUTENTIQUE_BIOMETRIA_PENDENTE" em
     * contracts.queue_error quando a API rejeita a criação do documento por conta
     * não homologada (user_biometric_verification_required).
     */
    const isBiometricPendingError =
      !!queueError &&
      (queueError.toLowerCase().includes('autentique_biometria_pendente') ||
        queueError.toLowerCase().includes('biometric_verification_required'));

    if (isBiometricPendingError) {
      return (
        <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          {/* Cabeçalho do erro */}
          <div className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Conta Autentique pendente de verificação biométrica</span>
          </div>
          {/* Passo a passo do que o usuário precisa fazer */}
          <div className="space-y-1 pl-6 text-xs text-muted-foreground">
            <p>
              A Autentique exige que o <strong>titular da conta</strong> conclua a verificação
              de identidade (biometria) antes de permitir o envio de documentos pela API.
              Enquanto isso não for feito, nenhum contrato deste tenant será enviado.
            </p>
            <p className="pt-1 font-medium text-foreground">Como resolver:</p>
            <ol className="list-decimal space-y-0.5 pl-4">
              <li>Acesse o painel da Autentique com o usuário titular da conta.</li>
              <li>Vá em <strong>Configurações → Verificação de identidade (biometria)</strong>.</li>
              <li>Conclua a validação enviando o documento e a selfie solicitados.</li>
              <li>Aguarde a aprovação da Autentique e volte aqui para reenviar o contrato.</li>
            </ol>
          </div>
          {/* Botões de ação */}
          <div className="flex flex-wrap gap-2 pl-6">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open('https://app.autentique.com.br/', '_blank', 'noopener,noreferrer')}
            >
              Abrir Painel Autentique
            </Button>
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={handleRetryError} disabled={isPending}>
                {isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                Tentar Novamente
              </Button>
            )}
          </div>
        </div>
      );
    }

    // Erro genérico — exibe também o motivo retornado pelo provider, quando houver

    return (
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex flex-col gap-0.5 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>Ocorreu um erro no processamento</span>
          </div>
          {queueError && (
            <span className="pl-6 text-xs text-muted-foreground break-words">{queueError}</span>
          )}
        </div>

        {canEdit && (
          <Button
            size="sm"
            onClick={handleRetryError}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <RefreshCw />
            )}
            {retryLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {/* ================================================================= */}
      {/* DRAFT: Gerar PDF */}
      {/* ================================================================= */}
      {status === 'draft' && (
        <>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => generatePdf.mutate()}
              disabled={generatePdf.isPending}
            >
              {generatePdf.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <FileText />
              )}
              Gerar PDF
            </Button>
          )}

          {/* Botão Excluir Rascunho com confirmação — gated por canDelete */}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deleteContract.isPending}
                >
                  {deleteContract.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 />
                  )}
                  Excluir Rascunho
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir rascunho?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir este contrato rascunho?
                    Esta ação é irreversível e o contrato será removido permanentemente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      deleteContract.mutate(undefined, {
                        onSuccess: () => onDeleteSuccess?.(),
                      });
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* GENERATED: Enviar para Assinatura */}
      {/* ================================================================= */}
      {status === 'generated' && (
        <>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => sendForSignature.mutate()}
              disabled={sendForSignature.isPending}
            >
              {sendForSignature.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              Enviar para Assinatura
            </Button>
          )}

          {/* Baixar PDF gerado antes de enviar para assinatura — leitura, sempre visível */}
          {pdfStoragePath && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadPdf.mutate({ pdfStoragePath })}
              disabled={downloadPdf.isPending}
            >
              {downloadPdf.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Download />
              )}
              Baixar PDF
            </Button>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* SENT: Consultar Status + Cancelar */}
      {/* ================================================================= */}
      {status === 'sent' && (
        <>
          {/* Botão reenviar link — gated por canEdit */}
          {canEdit && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => resendSignature.mutate(undefined)}
              disabled={resendSignature.isPending}
            >
              {resendSignature.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              Reenviar Link
            </Button>
          )}

          {/* Botão consultar status — gated por canEdit (envolve chamada ao provider) */}
          {canEdit && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => checkStatus.mutate()}
              disabled={checkStatus.isPending}
            >
              {checkStatus.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <RefreshCw />
              )}
              Consultar Status
            </Button>
          )}

          {/* Botão cancelar com confirmação — gated por canDelete */}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full sm:w-auto"
                  disabled={cancelContract.isPending}
                >
                  {cancelContract.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <XCircle />
                  )}
                  Cancelar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancelar contrato?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação é irreversível. O contrato será cancelado no provider
                    de assinatura e não poderá ser reativado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => cancelContract.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Confirmar Cancelamento
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* SIGNED: Baixar PDF Original + Ver PDF Assinado (link Autentique)  */}
      {/* RELEASE: v1.2.0 - 2026-02-18                                      */}
      {/* external_url vem do campo files.signed do webhook document.finished */}
      {/* da Autentique — URL permanente sem necessidade de Storage próprio   */}
      {/* ================================================================= */}
      {status === 'signed' && (
        <>
          {/* Botão para o PDF original (sem rubricas) */}
          {pdfStoragePath && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => downloadPdf.mutate({ pdfStoragePath, fileName: 'contrato-original.pdf' })}
              disabled={downloadPdf.isPending}
            >
              {downloadPdf.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Download />
              )}
              Baixar PDF Original
            </Button>
          )}

          {/* PDF assinado via link direto da Autentique (files.signed) */}
          {externalUrl ? (
            <Button
              size="xs"
              onClick={() => window.open(externalUrl, '_blank', 'noopener,noreferrer')}
            >
              <FileCheck />
              Ver PDF Assinado
            </Button>
          ) : (
            /* Link ainda não disponível — aguarda webhook document.finished */
            <span className="text-xs text-muted-foreground">
              PDF assinado será disponibilizado em breve
            </span>
          )}
        </>
      )}
    </div>
  );
}
